import { logger } from '@src/lib/logger';
import Keycloak, { type KeycloakInitOptions } from 'keycloak-js';

/**
 * Challenge-mode bootstrap (PRD #898, slice 1/#899): when the visitor has just
 * redeemed a `/challenge/start` token set, we initialize a Keycloak instance
 * under the public `therame-challenge` client instead of `therame-frontend`.
 * This is intentionally NARROW — only taken when the `challenge_active` flag
 * is present in localStorage — so it does not widen the dead-code-eliminated
 * dev localStorage bypass below.
 */
export const isChallengeBootstrap = window.localStorage.getItem('challenge_active') === 'true';

export const keycloakIns = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_ROOT,
  realm: 'therame',
  clientId: isChallengeBootstrap ? 'therame-challenge' : 'therame-frontend',
});

/**
 * Write the keycloak-js in-memory tokens back to the `challenge_*` localStorage
 * keys. The realm rotates refresh tokens (`revokeRefreshToken` with
 * `refreshTokenMaxReuse: 0`), so each refresh token is single-use. Critically,
 * `keycloak.init()` with a stored `token`+`refreshToken` ALWAYS forces a refresh
 * (`updateToken(-1)`) even when the access token is still valid — which consumes
 * the stored refresh token and rotates it in memory. Unless we persist the
 * rotated tokens, localStorage keeps the now-revoked refresh token, and the next
 * page reload fails its forced refresh with a 400 → keycloak.init returns false
 * → the router bounces the participant back to `/challenge` despite an
 * otherwise live session.
 */
const persistChallengeTokens = () => {
  if (keycloakIns.token) {
    window.localStorage.setItem('challenge_access_token', keycloakIns.token);
  }
  if (keycloakIns.refreshToken) {
    window.localStorage.setItem('challenge_refresh_token', keycloakIns.refreshToken);
  }
  if (keycloakIns.idToken) {
    window.localStorage.setItem('challenge_id_token', keycloakIns.idToken);
  }
};

// Registered at module load (before initializeKeycloak runs) so the forced
// refresh inside keycloak.init() — not just the later interval refreshes — also
// writes the rotated tokens back to localStorage.
if (isChallengeBootstrap) {
  keycloakIns.onAuthRefreshSuccess = persistChallengeTokens;
}

/**
 * Removes every `challenge_*` localStorage key written by the challenge
 * bootstrap (ChallengeLanding + persistChallengeTokens). Critically, this drops
 * the `challenge_active` flag that `isChallengeBootstrap` reads at module load:
 * until it is cleared, a full reload of `/` keeps booting the direct-grant-only
 * `therame-challenge` client with now-revoked tokens, so the router guard bounces
 * the visitor straight back to `/challenge` and they can never reach the normal
 * login form. Call this on every challenge auth-failure path.
 */
export const clearChallengeStorage = () => {
  window.localStorage.removeItem('challenge_active');
  window.localStorage.removeItem('challenge_access_token');
  window.localStorage.removeItem('challenge_refresh_token');
  window.localStorage.removeItem('challenge_id_token');
  window.localStorage.removeItem('challenge_session_id');
  window.localStorage.removeItem('challenge_challenge_id');
  window.localStorage.removeItem('challenge_consent_accepted');
};

const CHALLENGE_SESSION_EXPIRED_KEY = 'challenge_session_expired';

/**
 * One-shot signal that a challenge session ended *involuntarily* (token refresh
 * failed, a 401 came back, the router guard caught an unauthenticated state).
 * Stored in `sessionStorage` rather than a URL query param so it (a) survives the
 * full-page `window.location.replace` redirect that swaps the keycloak client
 * back, but (b) does NOT stick in the address bar across a manual reload — see
 * `consumeChallengeSessionExpired`. Deliberate logout never sets this, so the
 * "Sitzung abgelaufen" banner only appears when something actually expired.
 */
export const markChallengeSessionExpired = () => {
  window.sessionStorage.setItem(CHALLENGE_SESSION_EXPIRED_KEY, '1');
};

/**
 * Reads the expired signal and immediately clears it, so the banner shows once
 * and a subsequent reload of `/challenge` finds nothing. Returns whether the
 * flag was set.
 */
export const consumeChallengeSessionExpired = (): boolean => {
  const expired = window.sessionStorage.getItem(CHALLENGE_SESSION_EXPIRED_KEY) === '1';
  window.sessionStorage.removeItem(CHALLENGE_SESSION_EXPIRED_KEY);
  return expired;
};

/**
 * Initializes the Keycloak instance.
 * This function handles the core Keycloak setup and potential redirects.
 * @param keycloak The Keycloak instance.
 * @returns {Promise<boolean>} A promise that resolves to true if authenticated, false otherwise.
 */
export const initializeKeycloak = async (keycloak: Keycloak): Promise<boolean> => {
  try {
    // Build init options at runtime to properly read tokens from localStorage
    // (for Playwright/Cypress E2E tests that inject tokens before page load)
    const initOptions: KeycloakInitOptions = {
      scope: 'openid profile',
      pkceMethod: 'S256',
      checkLoginIframe: false,
    };

    // Use tokens from localStorage if they exist (for Playwright E2E tests
    // and the dev-only Impersonate flow). Gated on VITE_MODE so the read path
    // is dead-code-eliminated from prod/stage builds, closing a session-fixation
    // surface where a planted token could silently auto-auth the next visitor.
    const isDevOrTest = ['LOCAL', 'TESTING-FRONTEND'].includes(import.meta.env.VITE_MODE);
    if (isDevOrTest) {
      const accessToken = window.localStorage.getItem('access_token');
      const refreshToken = window.localStorage.getItem('refresh_token');
      const idToken = window.localStorage.getItem('id_token');

      if (accessToken) {
        initOptions.token = accessToken;
        logger.debug({ msg: 'Using access token from localStorage' });
        // Same single-use refresh tokens as the challenge client: without
        // writing the rotated ones back, the next full load (an office switch
        // reloads the app, #1822) fails its forced refresh and bounces to the login.
        if (!isChallengeBootstrap)
          keycloak.onAuthRefreshSuccess = () => {
            if (keycloak.token) window.localStorage.setItem('access_token', keycloak.token);
            if (keycloak.refreshToken) {
              window.localStorage.setItem('refresh_token', keycloak.refreshToken);
            }
            if (keycloak.idToken) window.localStorage.setItem('id_token', keycloak.idToken);
          };
      }
      if (refreshToken) {
        initOptions.refreshToken = refreshToken;
      }
      if (idToken) {
        initOptions.idToken = idToken;
      }
    }

    // Challenge bootstrap path: read tokens minted by POST /challenge/start.
    // This is active in production builds (unlike the dev localStorage bypass
    // above), but it is narrow by construction — only triggered after the
    // visitor has clicked through `/challenge` and submitted the shared access
    // code, and the keys live under a separate `challenge_*` prefix so the
    // generic dev keys can never seed it.
    if (isChallengeBootstrap) {
      const accessToken = window.localStorage.getItem('challenge_access_token');
      const refreshToken = window.localStorage.getItem('challenge_refresh_token');
      const idToken = window.localStorage.getItem('challenge_id_token');
      if (accessToken) {
        initOptions.token = accessToken;
      }
      if (refreshToken) {
        initOptions.refreshToken = refreshToken;
      }
      if (idToken) {
        initOptions.idToken = idToken;
      }
    }

    const authenticated = await keycloak.init(initOptions);
    return authenticated;
  } catch (error) {
    logger.error({ msg: 'Keycloak initialization failed:', error });
    return false;
  }
};

export const generateRegistrationLink = (token: string) => {
  return import.meta.env.VITE_API_ROOT + '/register/' + token;
};

/**
 * The registration form for a registration link's join page (#1834): it
 * registers a patient through the link, its „Zurück zum Login“ serves an
 * existing Account, and either way the login comes back to the join page.
 */
export const createJoinRegistrationUrl = async (token: string) => {
  const url = await keycloakIns.createRegisterUrl({
    redirectUri: `${window.location.origin}/join/${encodeURIComponent(token)}`,
  });
  return `${url}&role=THERAME_PATIENT&registrationLink=${encodeURIComponent(token)}`;
};

export default keycloakIns;
